import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Typography, SxProps, Theme } from '@mui/material';

type LegendItem = Readonly<{
  icon: React.ReactNode;
  label: string;
}>;

type ChartLegendProps = Readonly<{
  title: string;
  items: readonly LegendItem[];
  onInfoClick?: () => void;
  bordered?: boolean;
}>;

export const ChartLegend = ({ title, items, onInfoClick, bordered = true }: ChartLegendProps) => (
  <Box sx={containerSx(!!onInfoClick, bordered)} onClick={onInfoClick}>
    <Box sx={titleRowSx}>
      <Typography sx={titleSx}>{title}</Typography>
      {onInfoClick && <AppIcon iconName="InfoCircle" diameter="14px" color="Gray/400" />}
    </Box>
    {items.map((item) => (
      <Box key={item.label} sx={itemSx}>
        {item.icon}
        <Typography sx={labelSx}>{item.label}</Typography>
      </Box>
    ))}
  </Box>
);

const containerSx =
  (isClickable: boolean, bordered: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xxs'],
    alignItems: 'flex-start',
    flexShrink: 0,
    ...(bordered && {
      border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
      borderRadius: backrs.radius['radius-xl'],
      padding: backrs.spacing['spacing-md'],
      '@container (min-width: 400px)': {
        padding: backrs.spacing['spacing-lg'],
      },
      '@container (min-width: 600px)': {
        padding: backrs.spacing['spacing-2xl'],
      },
    }),
    ...(isClickable && { cursor: 'pointer' }),
  });

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  mb: backrs.spacing['spacing-xxs'],
  '@container (min-width: 400px)': {
    gap: backrs.spacing['spacing-xs'],
    mb: backrs.spacing['spacing-xs'],
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  typography: 'text-xs-bold',
  '@container (min-width: 400px)': {
    typography: 'text-sm-bold',
  },
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 400px)': {
    gap: backrs.spacing['spacing-lg'],
  },
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  typography: 'text-xs-regular',
  '@container (min-width: 400px)': {
    typography: 'text-sm-regular',
  },
});
